<template>
  <div class="call-item" :data-testid="`call-item-${call.id}`">
    <span class="call-avatar" :class="`call-avatar--${tone}`" aria-hidden="true">
      <i class="fas fa-phone"></i>
    </span>

    <div class="call-body">
      <div class="call-line">
        <span class="call-title">{{ title }}</span>
        <span class="call-date">{{ formatDateTime(call.call_date) }}</span>
      </div>

      <p v-if="call.notes" class="call-notes">{{ call.notes }}</p>

      <div class="call-meta">
        <span v-if="call.user_email">{{ call.user_email }}</span>
        <span v-if="call.loss_reason">{{ lossReasonText }}</span>
        <span v-if="call.voicemail">{{ VOICEMAIL_LABELS[call.voicemail] || call.voicemail }}</span>
        <span v-if="call.do_not_contact" class="call-meta-danger">Ne pas relancer</span>
        <span v-if="call.formation_nom">{{ call.formation_nom }}</span>
        <router-link
          v-if="showQuoteLink && call.quote_id"
          :to="`/quotes/${call.quote_id}`"
          @click.stop
        >
          Devis #{{ call.quote_human_id || call.quote_id }}
        </router-link>

        <span v-if="canEdit" class="call-actions">
          <button
            type="button"
            class="row-action"
            :data-testid="`call-edit-button-${call.id}`"
            @click="$emit('edit', call)"
          >
            Modifier
          </button>
          <button
            type="button"
            class="row-action row-action--danger"
            :data-testid="`call-delete-button-${call.id}`"
            @click="$emit('delete', call.id)"
          >
            Supprimer
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { formatDateTime } from '../../utils/dateFormatters.js'
import {
  CALL_DIRECTION_LABELS,
  LOSS_REASON_CATEGORY_LABELS,
  VOICEMAIL_LABELS
} from '../../constants/commercial-call.js'

const props = defineProps({
  call: {
    type: Object,
    required: true
  },
  canEdit: {
    type: Boolean,
    default: false
  },
  showQuoteLink: {
    type: Boolean,
    default: false
  }
})

defineEmits(['edit', 'delete'])

// Une ligne d'appel se lit comme une phrase : le sens de l'appel, puis son
// issue. Les détails (répondeur, motif de perte, auteur) passent en méta.
const title = computed(() => {
  const direction = CALL_DIRECTION_LABELS[props.call.direction] || props.call.direction || 'Appel'
  return props.call.result ? `${direction} — ${props.call.result}` : direction
})

// Motif de perte détaillé : « Catégorie › Libellé » (ex. « Budget › Prix trop élevé »).
const lossReasonText = computed(() => {
  const reason = props.call.loss_reason
  if (!reason) return ''
  const category = LOSS_REASON_CATEGORY_LABELS[reason.category]
  return category ? `${category} › ${reason.label}` : reason.label
})

// La pastille porte l'issue : perdu en rouge, joint en vert, reste en gris.
const tone = computed(() => {
  if (props.call.loss_reason || props.call.do_not_contact) return 'lost'
  if (props.call.voicemail) return 'neutral'
  if (props.call.result) return 'reached'
  return 'neutral'
})
</script>

<style scoped>
.call-item {
  display: flex;
  gap: var(--spacing-4);
  padding: var(--spacing-3) 0;
  border-bottom: 1px solid var(--color-border-light);
}

.call-item:last-child {
  border-bottom: 0;
}

.call-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-full);
  background: var(--color-bg-tertiary);
  color: var(--color-text-tertiary);
  font-size: 0.8rem;
  line-height: 1;
  flex-shrink: 0;
}

.call-avatar--reached {
  background: var(--color-success-lighter);
  color: var(--color-success-darker);
}

.call-avatar--lost {
  background: var(--color-danger-lighter);
  color: var(--color-danger-dark);
}

.call-body {
  flex: 1;
  min-width: 0;
}

.call-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-3);
}

.call-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.call-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.call-notes {
  margin: 3px 0 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: var(--line-height-normal);
  white-space: pre-wrap;
}

.call-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-1) var(--spacing-3);
  margin-top: var(--spacing-1);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.call-meta-danger {
  color: var(--color-danger-dark);
  font-weight: var(--font-weight-semibold);
}

.call-actions {
  display: flex;
  gap: var(--spacing-1);
  margin-left: auto;
}

@media (max-width: 768px) {
  .call-line {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>
